import React, { useState, useEffect } from "react";
import { describeError } from "./errors";
import { api } from "./api";
import "./EnvironmentFilesPanel.css";

/**
 * Single-file viewer / editor, rendered inside a Drawer so it has room to
 * work. The backend classifies the file (kind: text | table | image | pdf |
 * binary) and pre-converts office formats, so this component just renders:
 *   text   — editable (unless read_only, e.g. docx-extracted text)
 *   table  — spreadsheet grid with sheet tabs (xlsx)
 *   image / pdf — fetched as an authenticated blob and embedded
 *   binary — download fallback
 */
export default function EnvironmentFileViewer({
  path, envId, onRead, onWrite, onDelete, onChanged, onClose,
}) {
  const [entry, setEntry] = useState(null);
  const [content, setContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const dirty = editing && content !== originalContent;

  // `kind` is authoritative when present; older/cloud responses without it fall
  // back to the legacy shape (download_url => binary, else text content).
  const kind = entry?.kind || (entry?.download_url ? "binary" : "text");

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEditing(false);
    setEntry(null);
    setActiveSheet(0);
    onRead(path)
      .then((f) => {
        if (cancelled) return;
        setEntry(f || {});
        setContent(f?.content || "");
        setOriginalContent(f?.content || "");
      })
      .catch((e) => { if (!cancelled) setError(describeError(e) || "read failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path, onRead]);

  // Inline media (image/pdf): fetch through the authenticated download endpoint
  // into a blob URL — a plain <img src> cross-origin request can drop the auth
  // cookie depending on SameSite, so we fetch explicitly with credentials.
  useEffect(() => {
    if (!path || !envId || (kind !== "image" && kind !== "pdf")) return undefined;
    let revoked = false;
    let url = null;
    fetch(api.downloadEnvironmentFileUrl(envId, path), { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((b) => {
        url = URL.createObjectURL(b);
        if (revoked) URL.revokeObjectURL(url);
        else setBlobUrl(url);
      })
      .catch((e) => setError(describeError(e) || "preview failed"));
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
      setBlobUrl(null);
    };
  }, [path, envId, kind]);

  const handleSave = async () => {
    try {
      await onWrite(path, content);
      setOriginalContent(content);
      setEditing(false);
      if (onChanged) onChanged();
    } catch (e) {
      setError(describeError(e) || "save failed");
    }
  };

  const handleDelete = () => {
    if (!window.confirm(`Delete ${path}? This can't be undone.`)) return;
    onDelete(path)
      .then(() => { if (onChanged) onChanged(); onClose?.(); })
      .catch((e) => setError(describeError(e) || "delete failed"));
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = api.downloadEnvironmentFileUrl(envId, path);
    a.download = path.split("/").pop() || path;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const sheets = entry?.sheets || [];
  const sheet = sheets[activeSheet];

  return (
    <div className="efp-viewer">
      {error && <div className="efp-error">{error}</div>}
      <div className="efp-viewer-header">
        <span className="efp-viewer-path">{path}</span>
        <div className="efp-viewer-actions">
          {!editing && !loading && kind === "text" && !entry?.read_only && (
            <button className="ca-btn ca-btn--sm" onClick={() => setEditing(true)}>Edit</button>
          )}
          {editing && (
            <>
              <button
                className="ca-btn ca-btn--sm"
                onClick={() => { setContent(originalContent); setEditing(false); }}
              >Cancel</button>
              <button
                className="ca-btn ca-btn--sm ca-btn--primary"
                onClick={handleSave}
                disabled={!dirty}
              >Save</button>
            </>
          )}
          <button className="ca-btn ca-btn--sm" onClick={handleDownload}>Download</button>
          <button className="ca-btn ca-btn--sm ca-btn--danger" onClick={handleDelete}>Delete</button>
        </div>
      </div>

      {loading ? (
        <div className="efp-empty">Loading…</div>
      ) : kind === "image" ? (
        blobUrl
          ? <div className="efp-media"><img className="efp-media-img" src={blobUrl} alt={path} /></div>
          : <div className="efp-empty">Loading preview…</div>
      ) : kind === "pdf" ? (
        blobUrl
          ? <iframe className="efp-media-pdf" src={blobUrl} title={path} />
          : <div className="efp-empty">Loading preview…</div>
      ) : kind === "table" ? (
        <div className="efp-table-wrap">
          {sheets.length > 1 && (
            <div className="efp-sheet-tabs">
              {sheets.map((s, i) => (
                <button
                  key={s.name}
                  className={`efp-sheet-tab${i === activeSheet ? " active" : ""}`}
                  onClick={() => setActiveSheet(i)}
                >{s.name}</button>
              ))}
            </div>
          )}
          {sheet && sheet.rows.length > 0 ? (
            <div className="efp-table-scroll">
              <table className="efp-table">
                <tbody>
                  {sheet.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => <td key={ci}>{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="efp-empty">This sheet is empty.</div>
          )}
          {sheet?.truncated && (
            <div className="efp-table-note">
              Preview truncated — download the file for the full data.
            </div>
          )}
        </div>
      ) : kind === "binary" ? (
        <div className="efp-binary">
          <p>No inline preview for this file type.</p>
          <button className="ca-btn ca-btn--primary" onClick={handleDownload}>Download</button>
        </div>
      ) : editing ? (
        <textarea
          className="ca-textarea efp-editor"
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      ) : (
        <pre className="efp-content">{content}</pre>
      )}
    </div>
  );
}
